import { Icon } from "../components/Icon";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { href } from "../lib/route";
import { Workspace } from "../components/Shell";
import { LibraryTabs, useLibraryCounts } from "../components/LibraryTabs";
import { longDate } from "../lib/weekLine";
import {
  matchesProgramQuery, programCards, programSummary,
  type ProgramAssignmentCount, type ProgramCard, type ProgramDayCount, type ProgramRow,
} from "../lib/programList";

/**
 * PROGRAMS — the coach's multi-week blocks (W30(e), `docs/PROGRAMS-DESIGN.md` §4).
 *
 * A program is an ordered plan of TEMPLATE REFERENCES: which template, which week, which weekday.
 * It holds no workout content of its own, so nothing here can drift from the library — assigning
 * one calls `assign_coach_workout` once per day and every assigned day is a client-scoped copy of
 * its template, exactly as a hand assignment is (one source, 2026-08-27; copy-on-assign, V7-2).
 *
 * ⚠️ THE THIRD LIST IN THE **LIBRARY**, NOT A FOURTH RAIL ENTRY. A fourth section is PRODUCT
 * scope and HIS (`SectionId`'s own comment); a program is a library object made of library
 * objects, and Everfit's own rail reads `Library → Exercises · Workouts · Programs`
 * (`EVERFIT-MAP` §15-ter), so the third tab IS the copy rather than a compromise.
 *
 * ⚠️ WHAT THIS SCREEN ANSWERS — §2b's rule — is *which block do I want*. It does not assign: that
 * is the board's `Assign` sheet, where the block is already chosen and the client is the question.
 *
 * ⚠️ ARCHIVED BLOCKS ARE HIDDEN, NEVER DELETED (§2.1, mirroring W17d's `coach_workouts`). There
 * is no row delete in v1: a block a client is on is a record of what they were given.
 */
export function Programs({ reloadKey = 0, foreground = true }: {
  reloadKey?: number;
  /** False while a window is drawn over this screen — see the focus effect below. */
  foreground?: boolean;
}) {
  const [cards, setCards] = useState<ProgramCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  const libraryCounts = useLibraryCounts();

  /* Focus moves to the heading whenever this screen is the one in front: a hash navigation moves
     nothing on its own and would leave a keyboard user standing on a link that has gone. Keyed on
     `foreground` for the reason `Workouts` states — this screen can be a backdrop. */
  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      /* THREE READS, NOT AN EMBED, AND THAT IS DELIBERATE. `coach_program_days` and
         `coach_program_assignments` are counted ACROSS programs, and a PostgREST embed comes back
         UNORDERED and as object-or-array by inferred relationship (b4-2/3/10, paid for twice).
         Three flat reads of three narrow column sets are three predictable shapes, and the
         counting is a pure rule in `programList.ts` that a test can drive.
         ⚠️ EVERY READ IS SCOPED BY RLS, NOT BY AN `.eq()`. The three owner policies are the rule;
         a filter here would be a second statement of it and the one that could drift. */
      const [head, days, assigns] = await Promise.all([
        supabase.from("coach_programs")
          .select("id, name, description, updated_at")
          .is("archived_at", null)
          .order("updated_at", { ascending: false }),
        supabase.from("coach_program_days").select("program_id, week_index"),
        supabase.from("coach_program_assignments").select("program_id, client_id"),
      ]);
      if (cancelled) return;
      /* ⚠️ A FAILED READ IS NOT AN EMPTY LIST, AND THE COUNTS ARE THE HALF THAT MATTERS. If the
         day rows could not be read, every card would say `0 weeks` — a confident wrong number
         about somebody's work. So any of the three failing is ONE error state for the screen. */
      const failed = head.error ?? days.error ?? assigns.error;
      if (failed !== null && failed !== undefined) {
        setError(`Your programs could not be read. ${failed.message}`);
        return;
      }
      const programs: ProgramRow[] = (head.data ?? []).map((r) => ({
        id: r.id, name: r.name, description: r.description, updatedAtISO: r.updated_at,
      }));
      const dayRows: ProgramDayCount[] = (days.data ?? [])
        .map((r) => ({ programId: r.program_id, weekIndex: r.week_index }));
      const assignRows: ProgramAssignmentCount[] = (assigns.data ?? [])
        .map((r) => ({ programId: r.program_id, clientId: r.client_id }));
      setError(null);
      setCards(programCards(programs, dayRows, assignRows));
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const shown = (cards ?? []).filter((c) => matchesProgramQuery(c, query));

  return (
    <Workspace>
      <main id="main" className="workouts-page programs-page library-page">
        {/* W72 step 5 — the Library's ONE masthead (the roster's `ours-head`): the title, the one primary at its right. */}
        <header className="ours-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" tabIndex={-1} ref={heading}>Library</h1>
          </div>
          <a className="ht-button ht-button-primary" href={href({ name: "programNew" })}>
            New program
          </a>
        </header>
        {/* W72 step 5 — the Library's four parts as ONE row under the title; the left sub-nav and its phone copy are gone. */}
        <LibraryTabs current="programs" counts={libraryCounts} />

        {cards !== null && (
          <p className="ht-overline workouts-count">
            {`${String(shown.length)} ${shown.length === 1 ? "PROGRAM" : "PROGRAMS"}`}
          </p>
        )}


        <label className="workouts-search">
          <span className="ht-visually-hidden">Search programs</span>
          <span aria-hidden="true" className="workouts-search-glyph">
            <Icon name="search" size={16} />
          </span>
          <input className="ht-input workouts-search-input" type="search" value={query}
                 placeholder="Search programs"
                 onChange={(e) => setQuery(e.target.value)} />
        </label>

        {error !== null && <p className="ht-callout workouts-error" role="alert">{error}</p>}

        {cards === null && error === null && (
          <p className="ht-callout loading-line" aria-live="polite">Loading…</p>
        )}

        {cards !== null && cards.length === 0 && (
          <p className="ht-callout workouts-empty">
            No programs yet. A program is a block of weeks you plan once and give to any client
            from any Monday.
          </p>
        )}

        {/* A search that matches nothing says something DIFFERENT from an empty library — "No
            programs yet" under a typed query would tell a coach their blocks had gone. */}
        {cards !== null && cards.length > 0 && shown.length === 0 && (
          <p className="ht-callout workouts-empty">
            No program here matches &ldquo;{query.trim()}&rdquo;.
          </p>
        )}

        {shown.length > 0 && (
          /* ⚠️ ITS OWN `programs-*` PREFIX, GREPPED BEFORE IT WAS INVENTED. My first cut used
             `library-*`, which names nothing in any stylesheet — the opposite failure to the
             `results-*` collision that restyled a shipped control in both directions, and the
             same lesson: a class name is a vocabulary. The rows do NOT reuse `.workouts-row`,
             because that list carries a three-track grid sized by `--wk-ex` / `--wk-age` for
             columns this list does not have. */
          <ul className="programs-list">
            {shown.map((c) => (
              <li key={c.id} className="programs-row">
                <a className="programs-row-link" href={href({ name: "program", programId: c.id })}>
                  <span className="programs-name">{c.name}</span>
                  {/* BOTH NUMBERS, ALWAYS — `programSummary` prints `not assigned yet` rather
                      than omitting the phrase, because an omitted line cannot be told apart
                      from a card that does not show it. */}
                  <span className="programs-meta">{programSummary(c)}</span>
                  <span className="programs-when">Edited {longDate(c.updatedAtISO.slice(0, 10))}</span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </main>
    </Workspace>
  );
}
